/**
 * Organization Switcher Component
 *
 * Dropdown that shows the current org and allows switching between orgs.
 * Displayed in the top navigation bar.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';

const planBadgeColors: Record<string, string> = {
  free: 'bg-gray-100 text-gray-600',
  starter: 'bg-blue-100 text-blue-700',
  professional: 'bg-amber-100 text-amber-700',
  enterprise: 'bg-purple-100 text-purple-700',
};

export const OrgSwitcher: React.FC = () => {
  const { currentOrg, organizations, switchOrg, isLoading } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!currentOrg) return null;

  const hasMultipleOrgs = organizations.length > 1;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => hasMultipleOrgs && setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          hasMultipleOrgs
            ? 'hover:bg-gray-100 cursor-pointer'
            : 'cursor-default'
        }`}
        disabled={isLoading}
      >
        {/* Org avatar */}
        <div className="w-6 h-6 rounded bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
          {currentOrg.name.charAt(0).toUpperCase()}
        </div>

        {/* Org name */}
        <span className="max-w-[150px] truncate text-gray-900">
          {currentOrg.name}
        </span>

        {/* Plan badge */}
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${planBadgeColors[currentOrg.plan] || planBadgeColors.free}`}>
          {currentOrg.plan}
        </span>

        {/* Dropdown arrow */}
        {hasMultipleOrgs && (
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {/* Dropdown menu */}
      {isOpen && hasMultipleOrgs && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Switch Organization
          </div>
          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => {
                if (org.id !== currentOrg.id) {
                  switchOrg(org.id);
                }
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                org.id === currentOrg.id ? 'bg-blue-50' : ''
              }`}
            >
              <div className="w-8 h-8 rounded bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {org.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="font-medium text-gray-900 truncate">{org.name}</div>
                <div className="text-xs text-gray-500">{org.role}</div>
              </div>
              {org.id === currentOrg.id && (
                <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
